import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useManualSubmit } from '../hooks';

/**
 * Template18: ホラーQTE（HorrorQTE）
 * ホラー演出を重視したタイミングゲーム
 * - 背景はシナリオのものをそのまま使用（透過）
 * - 失敗するごとに画面端から暗くなっていく（ビネット効果）
 * - 心臓の鼓動に合わせて暗い部分が脈動する
 */
const Template18HorrorQTE = ({
  hostSubmit,
  hostConfig,
  registryId = 'minigame_horrorqte_18',
  templateId = 18
}) => {
  const config = hostConfig ?? {};
  const resolvedConfig = useMemo(() => {
    const safeNumber = (value, fallback) => (Number.isFinite(value) ? value : fallback);
    return {
      title: config.title ?? '',
      instruction: config.instruction ?? '気配が来たら、合図で動け',
      requiredSuccesses: safeNumber(config.requiredSuccesses, 4),
      totalAttempts: safeNumber(config.totalAttempts, 6),
      windowSizeMs: safeNumber(config.windowSizeMs, 600),
      perfectWindowMs: safeNumber(config.perfectWindowMs, 200),
      intervalMs: safeNumber(config.intervalMs, 2000),
      promptText: config.promptText ?? '逃げろ',
      successText: config.successText ?? '逃げ切った…',
      failText: config.failText ?? '捕まった…',
      // ホラー演出設定
      horror: {
        pulseColor: config.horror?.pulseColor ?? 'rgba(139, 0, 0, 0.6)',
        flashOnFail: config.horror?.flashOnFail !== false,
        shakeOnFail: config.horror?.shakeOnFail !== false,
        darkenOnStart: config.horror?.darkenOnStart !== false,
        showHeartbeat: config.horror?.showHeartbeat !== false
      }
    };
  }, [config]);

  const { submit, resetSubmission } = useManualSubmit(registryId, templateId, hostSubmit);

  const [gameState, setGameState] = useState('ready'); // 'ready' | 'waiting' | 'prompt' | 'result'
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [successes, setSuccesses] = useState(0);
  const [perfects, setPerfects] = useState(0);
  const [failures, setFailures] = useState(0); // 失敗回数をトラック
  const [attemptResult, setAttemptResult] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [promptStartTime, setPromptStartTime] = useState(null);
  const [shaking, setShaking] = useState(false);
  const [flashing, setFlashing] = useState(false);
  const [heartbeatPhase, setHeartbeatPhase] = useState(0);

  const promptTimeoutRef = useRef(null);
  const waitTimeoutRef = useRef(null);

  // 失敗に基づく暗さレベル（0~1）
  const maxFailures = resolvedConfig.totalAttempts - resolvedConfig.requiredSuccesses;
  const darknessLevel = useMemo(() => {
    if (maxFailures <= 0) return 0;
    return Math.min(failures / maxFailures, 1);
  }, [failures, maxFailures]);

  // 心臓の鼓動エフェクト
  useEffect(() => {
    if (gameState !== 'waiting' && gameState !== 'prompt') return;

    const interval = setInterval(() => {
      setHeartbeatPhase(prev => (prev + 1) % 4);
    }, 400);

    return () => clearInterval(interval);
  }, [gameState]);

  // 鼓動に合わせた脈動値（0~1）
  const pulseValue = useMemo(() => {
    // heartbeatPhase: 0,1,2,3 -> 0,1,2,3...
    // 脈動パターン: 収縮(0) -> 膨張(1) -> 収縮(2) -> 休止(3)
    if (heartbeatPhase === 0 || heartbeatPhase === 2) return 0;
    if (heartbeatPhase === 1) return 1;
    return 0.3; // 休止期
  }, [heartbeatPhase]);

  // プロンプト表示のタイムアウト処理
  useEffect(() => {
    if (gameState !== 'prompt') return;

    promptTimeoutRef.current = setTimeout(() => {
      // タイムアウト = 失敗
      handleResult('miss');
    }, resolvedConfig.windowSizeMs);

    return () => {
      if (promptTimeoutRef.current) {
        clearTimeout(promptTimeoutRef.current);
      }
    };
  }, [gameState, resolvedConfig.windowSizeMs]);

  // 次のプロンプトを表示
  const showNextPrompt = useCallback(() => {
    const randomDelay = 1000 + Math.random() * resolvedConfig.intervalMs;

    waitTimeoutRef.current = setTimeout(() => {
      setShowPrompt(true);
      setPromptStartTime(Date.now());
      setGameState('prompt');
    }, randomDelay);
  }, [resolvedConfig.intervalMs]);

  // 結果処理
  const handleResult = useCallback((result) => {
    if (promptTimeoutRef.current) {
      clearTimeout(promptTimeoutRef.current);
    }

    setAttemptResult(result);
    setShowPrompt(false);

    if (result === 'perfect') {
      setSuccesses(prev => prev + 1);
      setPerfects(prev => prev + 1);
    } else if (result === 'success') {
      setSuccesses(prev => prev + 1);
    } else {
      // 失敗回数を増加
      setFailures(prev => prev + 1);
      // 失敗時の演出
      if (resolvedConfig.horror.flashOnFail) {
        setFlashing(true);
        setTimeout(() => setFlashing(false), 150);
      }
      if (resolvedConfig.horror.shakeOnFail) {
        setShaking(true);
        setTimeout(() => setShaking(false), 300);
      }
    }

    const newAttempt = currentAttempt + 1;
    setCurrentAttempt(newAttempt);

    // 結果判定
    setTimeout(() => {
      setAttemptResult(null);

      if (newAttempt >= resolvedConfig.totalAttempts) {
        // ゲーム終了
        setGameState('result');
      } else {
        // 次のアテンプト
        setGameState('waiting');
        showNextPrompt();
      }
    }, 600);
  }, [currentAttempt, resolvedConfig, showNextPrompt]);

  // タップ処理
  const handleTap = useCallback(() => {
    if (gameState === 'prompt' && showPrompt) {
      const elapsed = Date.now() - promptStartTime;

      if (elapsed <= resolvedConfig.perfectWindowMs) {
        handleResult('perfect');
      } else if (elapsed <= resolvedConfig.windowSizeMs) {
        handleResult('success');
      } else {
        handleResult('miss');
      }
    } else if (gameState === 'waiting') {
      // 早押しペナルティ
      handleResult('early');
    }
  }, [gameState, showPrompt, promptStartTime, resolvedConfig, handleResult]);

  // ゲーム開始
  const startGame = useCallback(() => {
    setGameState('waiting');
    showNextPrompt();
  }, [showNextPrompt]);

  // 結果送信
  useEffect(() => {
    if (gameState !== 'result') return;

    const timer = setTimeout(() => {
      const finalResult = successes >= resolvedConfig.requiredSuccesses ? 'success' : 'fail';
      submit(finalResult, {
        status: finalResult,
        successes,
        perfects,
        failures,
        totalAttempts: currentAttempt
      });
    }, 1500);

    return () => clearTimeout(timer);
  }, [gameState, successes, perfects, failures, currentAttempt, resolvedConfig, submit]);

  // リセット
  useEffect(() => {
    setGameState('ready');
    setCurrentAttempt(0);
    setSuccesses(0);
    setPerfects(0);
    setFailures(0);
    setAttemptResult(null);
    setShowPrompt(false);
    setPromptStartTime(null);
    resetSubmission();

    return () => {
      if (promptTimeoutRef.current) clearTimeout(promptTimeoutRef.current);
      if (waitTimeoutRef.current) clearTimeout(waitTimeoutRef.current);
    };
  }, [resetSubmission]);

  const isFinalSuccess = successes >= resolvedConfig.requiredSuccesses;
  const isPlaying = gameState !== 'ready';

  // ビネット（周辺暗転）のサイズ計算
  // 失敗が増えるほど、中心の明るい部分が小さくなる
  // 脈動で暗い部分が広がったり縮んだりする
  const vignetteSize = useMemo(() => {
    // 基本サイズ: 100%（全く暗くない）から20%（ほぼ真っ暗）まで
    const baseSize = 100 - (darknessLevel * 80);
    // 脈動による変動: ±10%
    const pulseVariation = pulseValue * 15;
    return Math.max(5, baseSize - pulseVariation);
  }, [darknessLevel, pulseValue]);

  return (
    <div
      className={`t18-container ${shaking ? 'shake' : ''}`}
      onClick={gameState === 'waiting' || gameState === 'prompt' ? handleTap : undefined}
    >
      {/* ビネット効果オーバーレイ - 失敗ごとに周辺が暗くなり、心臓に合わせて脈動 */}
      {isPlaying && (
        <div
          className="t18-vignette"
          style={{
            '--vignette-size': `${vignetteSize}%`,
            '--darkness': darknessLevel,
          }}
        />
      )}

      {/* 赤フラッシュ */}
      {flashing && <div className="t18-flash" />}

      {/* メインコンテンツ */}
      <div className="t18-content">
        {/* タイトル */}
        {resolvedConfig.title && gameState === 'ready' && (
          <h2 className="t18-title">{resolvedConfig.title}</h2>
        )}

        {/* 心臓の鼓動インジケーター */}
        {resolvedConfig.horror.showHeartbeat && gameState !== 'ready' && gameState !== 'result' && (
          <div className="t18-heartbeat-container">
            <div className={`t18-heartbeat-pulse ${heartbeatPhase % 2 === 0 ? 'beat' : ''}`} />
          </div>
        )}

        {/* 進捗表示 */}
        {gameState !== 'ready' && (
          <div className="t18-progress">
            <span className="t18-progress-text">
              {currentAttempt} / {resolvedConfig.totalAttempts}
            </span>
          </div>
        )}

        {/* プロンプト表示 */}
        {showPrompt && (
          <div className="t18-prompt">
            <span className="t18-prompt-text">{resolvedConfig.promptText}</span>
          </div>
        )}

        {/* 結果表示 */}
        {attemptResult && (
          <div className={`t18-result ${attemptResult}`}>
            {attemptResult === 'perfect' && '...'}
            {attemptResult === 'success' && '...'}
            {attemptResult === 'miss' && '!!'}
            {attemptResult === 'early' && '...'}
          </div>
        )}

        {/* 待機中の指示 */}
        {gameState === 'waiting' && !showPrompt && !attemptResult && (
          <div className="t18-waiting">
            <span className="t18-waiting-text">{resolvedConfig.instruction}</span>
          </div>
        )}

        {/* スタートボタン */}
        {gameState === 'ready' && (
          <button onClick={startGame} className="t18-start-btn">
            息を潜める
          </button>
        )}

        {/* 最終結果 */}
        {gameState === 'result' && (
          <div className={`t18-final ${isFinalSuccess ? 'success' : 'fail'}`}>
            <p className="t18-final-text">
              {isFinalSuccess ? resolvedConfig.successText : resolvedConfig.failText}
            </p>
            <p className="t18-final-stats">
              {successes} / {resolvedConfig.requiredSuccesses}
            </p>
          </div>
        )}
      </div>

      <style>{`
        /* レイアウト/サイズ/キーフレーム/非色ユーティリティのみ。
           静的クロム色・意味的フィードバック色は global style.css の .t18-* に集約（mood トークン化）。
           データ駆動色（失敗ビネット rgba(0,0,0,...)・赤フラッシュ rgba(139,0,0,0.5)）はここに byte 不変で温存。 */
        .t18-container {
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          user-select: none;
          -webkit-tap-highlight-color: transparent;
          /* 背景は透過 - シナリオの背景がそのまま見える */
          background: transparent;
        }

        .t18-container.shake {
          animation: shake 0.3s ease-in-out;
        }

        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-8px); }
          50% { transform: translateX(8px); }
          75% { transform: translateX(-4px); }
        }

        /* ビネット効果: 周辺から暗くなる（darknessLevel/pulseValue 駆動＝データ駆動・温存） */
        .t18-vignette {
          position: absolute;
          inset: 0;
          pointer-events: none;
          /* radial-gradient: 中心は透明、外側は黒 */
          background: radial-gradient(
            ellipse at center,
            transparent 0%,
            transparent calc(var(--vignette-size, 80%) * 0.5),
            rgba(0, 0, 0, 0.3) calc(var(--vignette-size, 80%) * 0.7),
            rgba(0, 0, 0, 0.7) calc(var(--vignette-size, 80%) * 0.9),
            rgba(0, 0, 0, 0.95) 100%
          );
          transition: all 0.3s ease;
        }

        /* 失敗フラッシュ（flashOnFail 演出・データ駆動・温存） */
        .t18-flash {
          position: absolute;
          inset: 0;
          background: rgba(139, 0, 0, 0.5);
          pointer-events: none;
          animation: flash 0.15s ease-out;
        }

        @keyframes flash {
          0% { opacity: 1; }
          100% { opacity: 0; }
        }

        .t18-content {
          position: relative;
          z-index: 10;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 24px;
          padding: 20px;
        }

        .t18-title {
          font-size: 1.5rem;
          font-weight: bold;
          text-align: center;
        }

        .t18-heartbeat-container {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .t18-heartbeat-pulse {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          opacity: 0.5;
          transition: all 0.15s ease;
        }

        .t18-heartbeat-pulse.beat {
          width: 40px;
          height: 40px;
          opacity: 0.8;
        }

        .t18-progress {
          padding: 8px 16px;
          border-radius: 4px;
        }

        .t18-progress-text {
          font-size: 0.875rem;
          font-family: monospace;
        }

        .t18-prompt {
          animation: promptAppear 0.1s ease-out;
        }

        @keyframes promptAppear {
          0% { transform: scale(0.8); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }

        .t18-prompt-text {
          font-size: 3rem;
          font-weight: bold;
          animation: promptPulse 0.3s ease-in-out infinite;
        }

        @keyframes promptPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .t18-result {
          font-size: 1.5rem;
          font-weight: bold;
          animation: resultFade 0.6s ease-out;
        }

        @keyframes resultFade {
          0% { opacity: 1; transform: scale(1.2); }
          100% { opacity: 0.5; transform: scale(1); }
        }

        .t18-waiting {
          text-align: center;
        }

        .t18-waiting-text {
          font-size: 1rem;
          font-style: italic;
        }

        .t18-start-btn {
          padding: 16px 48px;
          font-size: 1.25rem;
          font-weight: bold;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .t18-start-btn:active {
          transform: scale(0.95);
        }

        .t18-final {
          text-align: center;
          animation: finalAppear 0.5s ease-out;
        }

        @keyframes finalAppear {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        .t18-final-text {
          font-size: 1.5rem;
          font-weight: bold;
          margin-bottom: 8px;
        }

        .t18-final-stats {
          font-size: 0.875rem;
        }
      `}</style>
    </div>
  );
};

export default Template18HorrorQTE;
